import DropTablesNav from "./DropTablesNav";
import AdLayout from "../../app-core/AdLayout";
import { makeCol, StatsTable } from "../../components/StatsTable2";
import { useState, useMemo } from "react";
import { FormControlLabel, Checkbox } from "@mui/material";
import * as T from "common/src/bindings/Translation2";

export const round2 = (v: number) => {
  const rounded = Math.round(v);
  if (Math.abs(v - rounded) < 0.05) {
    return rounded;
  } else {
    return Math.round(v * 10) / 10;
  }
};

const tierOrder: { [typ: string]: number } = {
  Silver: 0,
  Gold: 1,
  Prismatic: 2,
};

const tierClass: { [typ: string]: string } = {
  Silver: "border-white2",
  Gold: "border-amber-9",
  Prismatic: "border-teal-9",
};

const tierBg: { [typ: string]: string } = {
  Silver: "bg-white2",
  Gold: "bg-amber-9",
  Prismatic: "bg-teal-9",
};

const cols = [
  makeCol({
    width: 88,
    order: ([odds]) => odds,
    defaultDesc: true,
    renderTitle: (_, { t }) => <> {t("Odds")} </>,
    valueClass: "justify-center",
    renderValue: ([pct], totOdds) => (
      <> {round2((100 * pct) / totOdds) + "%"} </>
    ),
  }),
  makeCol({
    width: 100,
    order: ([odds, p1]) => tierOrder[p1] * 100 + odds,
    defaultDesc: true,
    titleClass: "text-[14px] leading-tight",
    renderTitle: (_, { t }) => <> {t("First Augment")}</>,
    valueClass: "justify-center",
    renderValue: ([_, a1, _2, _3]) => (
      <div className="flex items-center">
        <div
          className={`${tierBg[a1]} w-[10px] h-[10px] rounded-full mr-[5px] inline-block`}
        />
        {T.common(a1)}
      </div>
    ),
  }),
  makeCol({
    width: 100,
    order: ([odds, _1, p2]) => tierOrder[p2] * 100 + odds,
    defaultDesc: true,
    titleClass: "text-[14px] leading-tight",
    renderTitle: (_, { t }) => <> {t("Second Augment")} </>,
    valueClass: "justify-center",
    renderValue: ([_, _1, a2, _3]) => (
      <div className="flex items-center">
        <div
          className={`${tierBg[a2]} w-[10px] h-[10px] rounded-full mr-[5px] inline-block`}
        />
        {T.common(a2)}
      </div>
    ),
  }),
  makeCol({
    width: 100,
    order: ([odds, _1, _2, p3]) => tierOrder[p3] * 100 + odds,
    defaultDesc: true,
    titleClass: "text-[14px] leading-tight",
    renderTitle: (_, { t }) => <> {t("Third Augment")} </>,
    valueClass: "justify-center",
    renderValue: ([_, _1, _2, a3]) => (
      <div className="flex items-center">
        <div
          className={`${tierBg[a3]} w-[10px] h-[10px] rounded-full mr-[5px] inline-block`}
        />
        {T.common(a3)}
      </div>
    ),
  }),
];

type Tables = Array<[number, string, string, string]>;

export default function AugmentTables({ tables }: { tables: Tables }) {
  const [p1Silver, setP1Silver] = useState(false);
  const [p1Gold, setP1Gold] = useState(false);
  const [p1Prismatic, setP1Prismatic] = useState(false);
  const [p2Silver, setP2Silver] = useState(false);
  const [p2Gold, setP2Gold] = useState(false);
  const [p2Prismatic, setP2Prismatic] = useState(false);
  const [p3Silver, setP3Silver] = useState(false);
  const [p3Gold, setP3Gold] = useState(false);
  const [p3Prismatic, setP3Prismatic] = useState(false);

  const data = useMemo(() => {
    let data = tables;
    if (p1Silver || p1Gold || p1Prismatic) {
      data = data.filter(
        ([_, p1]) =>
          (p1Silver && p1 === "Silver") ||
          (p1Gold && p1 === "Gold") ||
          (p1Prismatic && p1 === "Prismatic"),
      );
    }
    if (p2Silver || p2Gold || p2Prismatic) {
      data = data.filter(
        ([_, _p1, p2]) =>
          (p2Silver && p2 === "Silver") ||
          (p2Gold && p2 === "Gold") ||
          (p2Prismatic && p2 === "Prismatic"),
      );
    }
    if (p3Silver || p3Gold || p3Prismatic) {
      data = data.filter(
        ([_, _p1, _p2, p3]) =>
          (p3Silver && p3 === "Silver") ||
          (p3Gold && p3 === "Gold") ||
          (p3Prismatic && p3 === "Prismatic"),
      );
    }

    return data;
    // if ()
  }, [
    tables,
    p1Silver,
    p1Gold,
    p1Prismatic,
    p2Silver,
    p2Gold,
    p2Prismatic,
    p3Silver,
    p3Gold,
    p3Prismatic,
  ]);

  const totOdds = data.reduce((acc, [odds]) => acc + odds, 0);
  const p1SilverOdds = data.reduce(
    (acc, [odds, p1]) => acc + (p1 === "Silver" ? odds : 0),
    0,
  );
  const p1GoldOdds = data.reduce(
    (acc, [odds, p1]) => acc + (p1 === "Gold" ? odds : 0),
    0,
  );
  const p1PrismaticOdds = data.reduce(
    (acc, [odds, p1]) => acc + (p1 === "Prismatic" ? odds : 0),
    0,
  );
  const p2SilverOdds = data.reduce(
    (acc, [odds, _p1, p2]) => acc + (p2 === "Silver" ? odds : 0),
    0,
  );
  const p2GoldOdds = data.reduce(
    (acc, [odds, _p1, p2]) => acc + (p2 === "Gold" ? odds : 0),
    0,
  );
  const p2PrismaticOdds = data.reduce(
    (acc, [odds, _p1, p2]) => acc + (p2 === "Prismatic" ? odds : 0),
    0,
  );
  const p3SilverOdds = data.reduce(
    (acc, [odds, _p1, _p2, p3]) => acc + (p3 === "Silver" ? odds : 0),
    0,
  );
  const p3GoldOdds = data.reduce(
    (acc, [odds, _p1, _p2, p3]) => acc + (p3 === "Gold" ? odds : 0),
    0,
  );
  const p3PrismaticOdds = data.reduce(
    (acc, [odds, _p1, _p2, p3]) => acc + (p3 === "Prismatic" ? odds : 0),
    0,
  );

  const hasP1Filter = p1Silver || p1Gold || p1Prismatic;
  const hasP2Filter = p2Silver || p2Gold || p2Prismatic;
  const hasP3Filter = p3Silver || p3Gold || p3Prismatic;

  return (
    <AdLayout>
      <div className="flex flex-col bg-bg rounded w-full md:w-[928px] pb-4">
        <DropTablesNav route={"Augments"} />
        <h2 className="h2 p-4 pb-6 pl-6 flex items-center gap-1">
          {T.common("Augments")}
        </h2>
        <div className="flex flex-col md:flex-row gap-8 md:w-[928px]">
          <div className="bg-bg rounded p-4 pl-6">
            <h3 className="h3">{T.common("Filters")}</h3>
            <div className="flex flex-col sm:flex-row md:flex-col gap-6 sm:gap-10 md:gap-6 pt-6">
              <div>
                <div className="font-medium font-montserrat pb-1">
                  First Augment
                </div>
                <div className="flex items-center">
                  <FormControlLabel
                    classes={{ root: "w-[110px]" }}
                    control={
                      <Checkbox
                        checked={p1Silver}
                        disabled={!hasP1Filter && p1SilverOdds === 0}
                        onChange={(e) => setP1Silver(e.target.checked)}
                      />
                    }
                    label={T.common("Silver")}
                  />
                  <div>{((100 * p1SilverOdds) / totOdds).toFixed()}%</div>
                </div>
                <div className="flex items-center">
                  <FormControlLabel
                    classes={{ root: "w-[110px]" }}
                    control={
                      <Checkbox
                        checked={p1Gold}
                        disabled={!hasP1Filter && p1GoldOdds === 0}
                        onChange={(e) => setP1Gold(e.target.checked)}
                      />
                    }
                    label={T.common("Gold")}
                  />
                  <div>{((100 * p1GoldOdds) / totOdds).toFixed()}%</div>
                </div>
                <div className="flex items-center">
                  <FormControlLabel
                    classes={{ root: "w-[110px]" }}
                    control={
                      <Checkbox
                        checked={p1Prismatic}
                        disabled={!hasP1Filter && p1PrismaticOdds === 0}
                        onChange={(e) => setP1Prismatic(e.target.checked)}
                      />
                    }
                    label={T.common("Prismatic")}
                  />
                  <div>{((100 * p1PrismaticOdds) / totOdds).toFixed()}%</div>
                </div>
              </div>
              <div>
                <div className="font-medium font-montserrat pb-1">
                  Second Augment
                </div>
                <div className="flex items-center">
                  <FormControlLabel
                    classes={{ root: "w-[110px]" }}
                    control={
                      <Checkbox
                        checked={p2Silver}
                        disabled={!hasP2Filter && p2SilverOdds === 0}
                        onChange={(e) => setP2Silver(e.target.checked)}
                      />
                    }
                    label={T.common("Silver")}
                  />
                  <div>{((100 * p2SilverOdds) / totOdds).toFixed()}%</div>
                </div>
                <div className="flex items-center">
                  <FormControlLabel
                    classes={{ root: "w-[110px]" }}
                    control={
                      <Checkbox
                        checked={p2Gold}
                        disabled={!hasP2Filter && p2GoldOdds === 0}
                        onChange={(e) => setP2Gold(e.target.checked)}
                      />
                    }
                    label={T.common("Gold")}
                  />
                  <div>{((100 * p2GoldOdds) / totOdds).toFixed()}%</div>
                </div>
                <div className="flex items-center">
                  <FormControlLabel
                    classes={{ root: "w-[110px]" }}
                    control={
                      <Checkbox
                        checked={p2Prismatic}
                        disabled={!hasP2Filter && p2PrismaticOdds === 0}
                        onChange={(e) => setP2Prismatic(e.target.checked)}
                      />
                    }
                    label={T.common("Prismatic")}
                  />
                  <div>{((100 * p2PrismaticOdds) / totOdds).toFixed()}%</div>
                </div>
              </div>
              <div>
                <div className="font-medium font-montserrat pb-1">
                  Third Augment
                </div>
                <div className="flex items-center">
                  <FormControlLabel
                    classes={{ root: "w-[110px]" }}
                    control={
                      <Checkbox
                        checked={p3Silver}
                        disabled={!hasP3Filter && p3SilverOdds === 0}
                        onChange={(e) => setP3Silver(e.target.checked)}
                      />
                    }
                    label={T.common("Silver")}
                  />
                  <div>{((100 * p3SilverOdds) / totOdds).toFixed()}%</div>
                </div>
                <div className="flex items-center">
                  <FormControlLabel
                    classes={{ root: "w-[110px]" }}
                    control={
                      <Checkbox
                        checked={p3Gold}
                        disabled={!hasP3Filter && p3GoldOdds === 0}
                        onChange={(e) => setP3Gold(e.target.checked)}
                      />
                    }
                    label={T.common("Gold")}
                  />
                  <div>{((100 * p3GoldOdds) / totOdds).toFixed()}%</div>
                </div>
                <div className="flex items-center">
                  <FormControlLabel
                    classes={{ root: "w-[110px]" }}
                    control={
                      <Checkbox
                        checked={p3Prismatic}
                        disabled={!hasP3Filter && p3PrismaticOdds === 0}
                        onChange={(e) => setP3Prismatic(e.target.checked)}
                      />
                    }
                    label={T.common("Prismatic")}
                  />
                  <div>{((100 * p3PrismaticOdds) / totOdds).toFixed()}%</div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex-1 bg-bg rounded sm:ml-6 sm:mr-4">
            <StatsTable
              stickyColCount={1}
              data={data}
              cols={cols}
              keyFn={([_, a1, a2, a3]) => a1 + a2 + a3}
              top="tm40"
              context={totOdds}
            />
          </div>
        </div>
      </div>
    </AdLayout>
  );
}
